// Smooth scroll controller using Lenis
// Handles main page, page overlays, project overlays, and fullscreen slider compatibility

import { utility_controller } from '../utility/utility_controller'

var defaultEasingFn = function (t) {
	return Math.min(1, 1.001 - Math.pow(2, -10 * t))
}

var bodyLenis = null
var bodyRafId = null
var bodyLocks = {}
var fullscreenSliderLockActive = false
var bodyIsFixed = false
var overlayLenisInstances = {
	project: null,
	page: null,
	search: null,
}

// Idempotency guards: each bind* function attaches laytheme.on listeners
// that must NEVER be registered twice (handlers would fire twice per event,
// duplicating createOverlayLenis / addBodyLock work). initSmoothScroll runs
// these from both `i()` and from itself on every newpageshown — so the guards
// are essential to prevent listener stacking.
var overlayEventsBound = false
var fullscreenSliderEventsBound = false
var resizeEventsBound = false
var newPageShownEventBound = false
var iframeWheelPassthroughEventsBound = false
var iframeWheelPassthroughTimeout = null
var iframeWheelPassthroughLastIframe = null
var iframeWheelPassthroughLastClearTime = 0

var iframeWheelPassthroughClass = 'lay-smooth-scroll-iframe-wheel-passthrough'
var iframeWheelPassthroughSelector = 'body > .lay-content iframe, #lay-project-overlay iframe, .lay-overlay iframe, .search-view iframe'

var emitSmoothScroll = function (type, instance) {
	if (window.laytheme && typeof window.laytheme.emit === 'function') {
		window.laytheme.emit('smoothscroll', { type: type, instance: instance })
	}
}

var bindLenisScrollEvent = function (type, instance) {
	if (!instance || typeof instance.on !== 'function') {
		return
	}
	instance.on('scroll', function () {
		emitSmoothScroll(type, instance)
	})
}

var isAppleDevice = function () {
	var ua = navigator.userAgent || ''
	var platform = navigator.platform || ''
	return /Macintosh|Mac OS X|iPhone|iPad|iPod/i.test(ua) || platform.toLowerCase().indexOf('mac') >= 0
}

// True when smooth scroll is on and (not "disable on Mac" or not on Mac)
var isSmoothScrollEnabled = function () {
	if (typeof layData === 'undefined' || layData.misc_options_smooth_scroll != 'on') {
		return false
	}
	if (layData.misc_options_smooth_scroll_disable_mac == 'on') {
		if (isAppleDevice()) {
			return false
		}
	}
	return true
}

var getLayContentForElement = function (element) {
	if (!element) {
		return null
	}
	if (element.classList && element.classList.contains('lay-content')) {
		return element
	}
	return element.querySelector ? element.querySelector('.lay-content') : null
}

var isSmoothScrollEnabledForElement = function (element) {
	if (!isSmoothScrollEnabled()) {
		return false
	}
	var layContent = getLayContentForElement(element)
	return !layContent || layContent.getAttribute('data-disable-smooth-scroll') !== 'on'
}

var isBodySmoothScrollEnabled = function () {
	return isSmoothScrollEnabledForElement(document.querySelector('body > .lay-content'))
}

var createLenisOptions = function (overrides) {
	var options = {
		duration: 1.2,
		easing: defaultEasingFn,
		orientation: 'vertical',
		gestureOrientation: 'vertical',
		smoothWheel: true,
		wheelMultiplier: 1,
		smoothTouch: false,
		touchMultiplier: 2,
		infinite: false,
		anchors: false,
	}
	if (overrides) {
		for (var key in overrides) {
			if (overrides.hasOwnProperty(key)) {
				options[key] = overrides[key]
			}
		}
	}
	return options
}

var hasDataPreventAttributes = function (node) {
	if (!node || !node.hasAttribute) {
		return false
	}
	if (node.hasAttribute('data-lenis-prevent')) {
		return true
	}
	if (node.hasAttribute('data-lenis-prevent-wheel')) {
		return true
	}
	if (node.hasAttribute('data-lenis-prevent-touch')) {
		return true
	}
	return false
}

var isNodeInsideOverlay = function (node) {
	return jQuery(node).closest('.lay-overlay.display-block-overlay, #lay-project-overlay, .search-view.show').length > 0
}

var isNodeInsideFullscreenSlider = function (node) {
	return jQuery(node).closest('.vertical-fullpage, .horizontal-fullpage').length > 0
}

var isHorizontalScrollActive = function () {
	var html = document.documentElement
	return !!(html && (html.classList.contains('horizontal-scroll-active') || html.getAttribute('data-horizontal-scroll-active') === '1'))
}

var bodyPreventFunction = function (node) {
	if (isNodeInsideOverlay(node)) {
		return true
	}
	if (isNodeInsideFullscreenSlider(node)) {
		return true
	}
	// When horizontal scroll is active, let horizontal_scroll_controller handle wheel (don't use Lenis for vertical scroll)
	if (isHorizontalScrollActive()) {
		return true
	}
	return hasDataPreventAttributes(node)
}

var overlayPreventFunction = function (node) {
	return hasDataPreventAttributes(node)
}

var hasActiveLenisInstance = function () {
	if (bodyLenis && !bodyLenis.isStopped) {
		return true
	}
	for (var key in overlayLenisInstances) {
		if (overlayLenisInstances.hasOwnProperty(key) && overlayLenisInstances[key] && !overlayLenisInstances[key].isStopped) {
			return true
		}
	}
	return false
}

var clearIframeWheelPassthrough = function () {
	if (iframeWheelPassthroughTimeout) {
		clearTimeout(iframeWheelPassthroughTimeout)
		iframeWheelPassthroughTimeout = null
	}
	iframeWheelPassthroughLastClearTime = Date.now()
	if (document.documentElement) {
		document.documentElement.classList.remove(iframeWheelPassthroughClass)
	}
}

var enableIframeWheelPassthrough = function (duration) {
	if (
		!isSmoothScrollEnabled() ||
		!document.documentElement ||
		(!hasActiveLenisInstance() && !document.documentElement.classList.contains('lenis'))
	) {
		return
	}

	document.documentElement.classList.add(iframeWheelPassthroughClass)

	if (iframeWheelPassthroughTimeout) {
		clearTimeout(iframeWheelPassthroughTimeout)
	}
	iframeWheelPassthroughTimeout = setTimeout(clearIframeWheelPassthrough, duration)
}

var bindIframeWheelPassthroughEvents = function () {
	if (iframeWheelPassthroughEventsBound || typeof document === 'undefined') {
		return
	}
	iframeWheelPassthroughEventsBound = true

	document.addEventListener(
		'mouseover',
		function (event) {
			if (!event.target || !event.target.closest) {
				return
			}
			var iframe = event.target.closest(iframeWheelPassthroughSelector)
			if (!iframe) {
				return
			}
			if (iframe === iframeWheelPassthroughLastIframe && Date.now() - iframeWheelPassthroughLastClearTime < 150) {
				return
			}
			iframeWheelPassthroughLastIframe = iframe
			enableIframeWheelPassthrough(900)
		},
		true
	)

	document.addEventListener(
		'wheel',
		function () {
			enableIframeWheelPassthrough(300)
		},
		{ passive: true, capture: true }
	)
}

var hasBodyLocks = function () {
	for (var key in bodyLocks) {
		if (bodyLocks.hasOwnProperty(key)) {
			return true
		}
	}
	return false
}

var addBodyLock = function (reason) {
	if (!reason) {
		return
	}

	var wasLocked = hasBodyLocks()
	bodyLocks[reason] = true

	// Apply position: fixed if this is the first lock
	// Use the unified fixBody function from utility_controller (it handles Lenis internally)
	if (!wasLocked) {
		bodyIsFixed = true
		utility_controller.fixBody(true, reason)
	}
}

var removeBodyLock = function (reason) {
	if (reason && bodyLocks[reason]) {
		delete bodyLocks[reason]
	}

	// Remove position: fixed if this was the last lock
	// Use the unified unfixBody function from utility_controller (it handles Lenis internally)
	if (!hasBodyLocks() && bodyIsFixed) {
		bodyIsFixed = false
		utility_controller.unfixBody()
	}
}

var startBodyRafLoop = function () {
	var raf = function (time) {
		if (!bodyLenis) {
			return
		}
		bodyLenis.raf(time)
		bodyRafId = requestAnimationFrame(raf)
	}
	bodyRafId = requestAnimationFrame(raf)
}

var destroyBodyLenis = function () {
	if (bodyLenis) {
		bodyLenis.destroy()
		bodyLenis = null
	}
	if (bodyRafId) {
		cancelAnimationFrame(bodyRafId)
		bodyRafId = null
	}
	window.lenisInstance = null
	clearIframeWheelPassthrough()
}

var destroyOverlayLenis = function (type) {
	if (!overlayLenisInstances[type]) {
		return
	}
	overlayLenisInstances[type].destroy()
	overlayLenisInstances[type] = null
}

var isScrollSliderActive = function () {
	// Use the scrollslider's own function to determine if it's active
	if (window.scrollSlider && typeof window.scrollSlider.isActiveHere === 'function') {
		return window.scrollSlider.isActiveHere()
	}
	// Fallback to checking the class if scrollslider function is not available
	return jQuery('html').hasClass('scrollslider-active')
}

var isFullscreenSliderActive = function () {
	if (window.magneticSlides && typeof window.magneticSlides.isActiveHere === 'function') {
		var isActive = window.magneticSlides.isActiveHere()
		return isActive
	}
	return false
}

var createOverlayLenis = function (type, overlayElement) {
	// Replacing one overlay with another must also remove the previous instance
	// when the newly opened page has disabled Smooth Scroll.
	destroyOverlayLenis(type)

	var smoothScrollEnabledHere = type === 'search' ? isSmoothScrollEnabled() : isSmoothScrollEnabledForElement(overlayElement)
	if (!smoothScrollEnabledHere) {
		return null
	}

	if (!overlayElement) {
		return null
	}

	// Project overlay: `#lay-project-overlay` itself has overflow-y:scroll, so the
	// wrapper is the overlay element and the inner `.lay-content` is the content.
	// Page overlay: `.lay-overlay > .lay-content` is the scrollable element
	// (overflow-y:auto, max-height:100vh in overlays.scss), so wrapper === that
	// .lay-content. Wrapper === content is fine for Lenis.
	// Search overlay: `.search-view` has overflow-y:scroll directly and has no
	// `.lay-content` wrapper — wrapper === content === the search-view itself.
	var wrapper, content
	if (type === 'page') {
		wrapper = overlayElement.querySelector('.lay-content') || overlayElement
		content = wrapper
	} else if (type === 'search') {
		wrapper = overlayElement
		content = overlayElement
	} else {
		wrapper = overlayElement
		content = overlayElement.querySelector('.lay-content') || overlayElement
	}

	if (!wrapper || !content) {
		return null
	}

	// Check if scrollslider is active in the overlay content itself (not the main page)
	var $content = jQuery(content)
	if ($content.hasClass('scrollslider-active') || $content.hasClass('infinite-scrollslider-active')) {
		return null
	}

	// Note: We only check scrollslider status in the overlay content itself, not the main page.
	// This allows Lenis to be used in overlays even if scrollslider is active on the main page.
	// The class check above is sufficient - if scrollslider is active in overlay, it will have the class.

	if (typeof Lenis === 'undefined' || !Lenis) {
		console.error('[createOverlayLenis] Lenis is not available!')
		return null
	}

	// Note: We don't check isScrollSliderActive() here because we only care about
	// scrollslider status in the overlay content, not the main page.
	// If scrollslider is active on main page but not in overlay, we still want Lenis in overlay.

	// According to Lenis docs: wrapper is the scrollable container, content is the content inside
	// For nested scroll, we need wrapper and content to be different elements
	var lenisOptions = createLenisOptions({
		wrapper: wrapper,
		content: content,
		eventsTarget: wrapper,
		prevent: overlayPreventFunction,
		autoRaf: true,
		allowNestedScroll: true,
	})

	try {
		// Use window.Lenis explicitly to ensure we're using the global reference
		var LenisConstructor = window.Lenis || Lenis
		if (!LenisConstructor || typeof LenisConstructor !== 'function') {
			console.error('[createOverlayLenis] Lenis constructor is not available')
			return null
		}

		// Ensure wrapper and content are in the DOM
		if (!document.body.contains(wrapper) || !document.body.contains(content)) {
			console.warn('[createOverlayLenis] Wrapper or content is not in DOM yet')
			return null
		}

		overlayLenisInstances[type] = new LenisConstructor(lenisOptions)
		bindLenisScrollEvent(type + '-overlay', overlayLenisInstances[type])
	} catch (error) {
		console.error('[createOverlayLenis] Error creating Lenis instance:', error)
		console.error('[createOverlayLenis] Error stack:', error.stack)
		console.error('[createOverlayLenis] Lenis options that failed:', lenisOptions)
		// Don't throw - just return null to prevent breaking the overlay
		return null
	}

	updateOverlayLenisForFullscreenSlider()
	return overlayLenisInstances[type]
}

var handlePageOverlayShown = function ($overlay) {
	if (!$overlay || $overlay.length === 0) {
		return
	}

	var disableScroll = $overlay.attr('data-disable-scroll') == 'on'

	// Smooth scrolling inside the overlay is independent of the per-overlay
	// "Disable scrolling" setting — that setting only controls whether the
	// underlying page (body) is locked. The overlay gets its own Lenis unless
	// its page-level Smooth Scroll override is enabled.
	// `bodyPreventFunction` already filters wheel events that originate inside
	// an overlay, so body Lenis won't fight with the overlay Lenis.
	setTimeout(function () {
		createOverlayLenis('page', $overlay[0])
	}, 50)

	// Body lock follows only the per-overlay setting now (matches the
	// non-smooth-scroll path). When `data-disable-scroll` is off, the body
	// stays scrollable underneath the overlay — wheel outside the overlay
	// scrolls the page (smoothly via body Lenis if enabled).
	if (disableScroll) {
		addBodyLock('page-overlay')
	}
}

var handlePageOverlayHidden = function () {
	// Only tear down if no overlays are still open (switching overlays doesn't emit overlayhidden).
	if (jQuery('.lay-overlay.lay-show-overlay').length === 0) {
		destroyOverlayLenis('page')
		removeBodyLock('page-overlay')
	}
}

var handleProjectOverlayShown = function () {
	if (isSmoothScrollEnabled()) {
		addBodyLock('project-overlay')
	}
	// Delay slightly to ensure overlay is fully rendered and DOM is ready
	setTimeout(function () {
		var overlayElement = document.querySelector('#lay-project-overlay')

		createOverlayLenis('project', overlayElement)
	}, 50)
}

var handleProjectOverlayClosed = function () {
	destroyOverlayLenis('project')
	removeBodyLock('project-overlay')
}

// Search overlay (`.search-view`) — same pattern as project / page overlay:
// when smooth scroll is enabled, spin up a dedicated Lenis instance on the
// search-view so wheel events scroll the result list smoothly. body Lenis
// already filters wheels inside the search overlay via the extended
// `isNodeInsideOverlay` check, so the two Lenis instances don't fight.
var handleSearchOverlayShown = function () {
	if (!isSmoothScrollEnabled()) {
		return
	}
	setTimeout(function () {
		var overlayElement = document.querySelector('.search-view')
		if (overlayElement) {
			createOverlayLenis('search', overlayElement)
		}
	}, 50)
}

var handleSearchOverlayHidden = function () {
	if (isSmoothScrollEnabled()) {
		destroyOverlayLenis('search')
	}
}

var updateOverlayLenisForFullscreenSlider = function () {
	// Only update if smooth scroll is enabled
	if (!isSmoothScrollEnabled()) {
		return
	}

	// Project overlay
	if (overlayLenisInstances.project) {
		var projectOverlayEl = document.querySelector('#lay-project-overlay')
		if (projectOverlayEl) {
			var hasSliderProject = jQuery(projectOverlayEl).find('.vertical-fullpage, .horizontal-fullpage').length > 0
			if (hasSliderProject) {
				overlayLenisInstances.project.stop()
			} else {
				overlayLenisInstances.project.start()
			}
		}
	}

	// Page overlay (currently-shown one). Mirror the project-overlay behavior:
	// pause Lenis if the overlay's content includes a fullscreen slider.
	if (overlayLenisInstances.page) {
		var $shownPageOverlay = jQuery('.lay-overlay.lay-show-overlay').first()
		if ($shownPageOverlay.length > 0) {
			var hasSliderPage = $shownPageOverlay.find('.vertical-fullpage, .horizontal-fullpage').length > 0
			if (hasSliderPage) {
				overlayLenisInstances.page.stop()
			} else {
				overlayLenisInstances.page.start()
			}
		}
	}
}

var updateFullscreenSliderState = function () {
	// Only update if smooth scroll is enabled
	if (!isSmoothScrollEnabled()) {
		return
	}

	// Check if fullscreen slider (javascript type) is active
	var fullscreenSliderActive = isFullscreenSliderActive()
	
	// If fullscreen slider is active and smooth scroll is initialized, destroy it
	if (fullscreenSliderActive && bodyLenis) {
		bodyLenis.destroy()
		bodyLenis = null
		if (bodyRafId) {
			cancelAnimationFrame(bodyRafId)
			bodyRafId = null
		}
		fullscreenSliderLockActive = false
		return
	}

	if (!bodyLenis) {
		return
	}
	// Check for both fullpage.js (.vertical-fullpage, .horizontal-fullpage) and CSS scroll snap (.vertical-scrollsnap, .horizontal-scrollsnap)
	var hasSlider = jQuery('.vertical-fullpage, .horizontal-fullpage, .vertical-scrollsnap, .horizontal-scrollsnap').length > 0
	if (hasSlider && !fullscreenSliderLockActive) {
		fullscreenSliderLockActive = true
		addBodyLock('fullscreen-slider')
	} else if (!hasSlider && fullscreenSliderLockActive) {
		fullscreenSliderLockActive = false
		removeBodyLock('fullscreen-slider')
	}
	updateOverlayLenisForFullscreenSlider()
}

var bindOverlayEvents = function () {
	if (!window.laytheme) {
		return
	}
	if (overlayEventsBound) {
		return
	}
	overlayEventsBound = true

	window.laytheme.on('overlayshown', function ($overlay) {
		handlePageOverlayShown($overlay)
	})

	window.laytheme.on('overlayhidden', function () {
		handlePageOverlayHidden()
	})

	window.laytheme.on('project_overlay_before_animate_in', function () {
		handleProjectOverlayShown()
	})

	window.laytheme.on('projectoverlayclosed', function () {
		handleProjectOverlayClosed()
	})

	window.laytheme.on('destroyoverlay', function () {
		handleProjectOverlayClosed()
	})

	window.laytheme.on('searchshown', function () {
		handleSearchOverlayShown()
	})

	window.laytheme.on('searchhidden', function () {
		handleSearchOverlayHidden()
	})

	// Search results arrive asynchronously (AJAX) AFTER the search overlay
	// Lenis was created — at init time `.search-view` only contains the
	// input, so Lenis computes `limit: 0` and refuses to scroll. Call
	// `.resize()` whenever the results region is repopulated so Lenis
	// re-reads the new scrollHeight and the scroll range becomes available.
	window.laytheme.on('searchresultshown', function () {
		if (overlayLenisInstances.search && typeof overlayLenisInstances.search.resize === 'function') {
			overlayLenisInstances.search.resize()
		}
	})
}

var bindFullscreenSliderEvents = function () {
	// Only bind if Smooth Scroll is active for the normal page.
	if (!isBodySmoothScrollEnabled()) {
		return
	}
	if (fullscreenSliderEventsBound) {
		return
	}
	fullscreenSliderEventsBound = true

	var trigger = function () {
		updateFullscreenSliderState()
	}

	if (window.laytheme) {
		window.laytheme.on('newpageshown', function () {
			setTimeout(trigger, 100)
		})
	}

	if (jQuery(window)) {
		jQuery(window).on('fpAfterLoad fpAfterSlideLoad fpAfterRender fpDestroy', function () {
			setTimeout(trigger, 0)
		})
	}

	setTimeout(trigger, 100)
}

var bindResizeEvents = function () {
	// Only bind if smooth scroll is enabled
	if (!isSmoothScrollEnabled()) {
		return
	}
	if (resizeEventsBound) {
		return
	}
	resizeEventsBound = true

	jQuery(window).on('resize', function () {
		if (bodyLenis) {
			bodyLenis.resize()
		}
		if (overlayLenisInstances.project && overlayLenisInstances.project.resize) {
			overlayLenisInstances.project.resize()
		}
		if (overlayLenisInstances.search && overlayLenisInstances.search.resize) {
			overlayLenisInstances.search.resize()
		}
		if (overlayLenisInstances.page && overlayLenisInstances.page.resize) {
			overlayLenisInstances.page.resize()
		}
	})
}

var initSmoothScroll = function () {
	if (!isBodySmoothScrollEnabled()) {
		destroyBodyLenis()
		return
	}

	if (typeof Lenis === 'undefined' || !Lenis) {
		console.warn('Lenis is not loaded. Make sure lenis.min.js is in vendor files.')
		return
	}

	// Don't initialize Lenis if scrollslider is active
	if (isScrollSliderActive()) {
		// Destroy existing bodyLenis if scrollslider is now active
		if (bodyLenis) {
			bodyLenis.destroy()
			bodyLenis = null
			if (bodyRafId) {
				cancelAnimationFrame(bodyRafId)
				bodyRafId = null
			}
		}
		return
	}

	// Don't initialize Lenis if fullscreen slider (javascript type) is active
	if (isFullscreenSliderActive()) {
		// Destroy existing bodyLenis if fullscreen slider is now active
		if (bodyLenis) {
			bodyLenis.destroy()
			bodyLenis = null
			if (bodyRafId) {
				cancelAnimationFrame(bodyRafId)
				bodyRafId = null
			}
		}
		return
	}

	// If bodyLenis already exists, don't create a new one
	if (bodyLenis) {
		return
	}

	try {
		bodyLenis = new Lenis(
			createLenisOptions({
				prevent: bodyPreventFunction,
			})
		)
		bindLenisScrollEvent('body', bodyLenis)

		startBodyRafLoop()
		window.lenisInstance = bodyLenis
		window.lenisOverlayInstances = overlayLenisInstances

		bindOverlayEvents()
		bindFullscreenSliderEvents()
		bindResizeEvents()
	} catch (error) {
		console.error('[initSmoothScroll] Error creating Lenis instance:', error)
		console.error('[initSmoothScroll] Error stack:', error.stack)
		bodyLenis = null
	}
}

var bindNewPageShownEvent = function () {
	if (!window.laytheme) {
		return
	}
	if (newPageShownEventBound) {
		return
	}
	newPageShownEventBound = true

	window.laytheme.on('newpageshown', function () {
		// Use setTimeout to ensure scrollslider has time to initialize
		setTimeout(function () {
			initSmoothScroll()
		}, 0)
	})
}

var smooth_scroll_controller = {
	i: function () {
		// Initialize on first page load
		initSmoothScroll()
		// Set up listener for subsequent page loads
		bindNewPageShownEvent()
		// Bind overlay events even if Lenis is not initialized (for body lock functionality)
		bindOverlayEvents()
		// An overlay may still use Smooth Scroll when the normal page disables it.
		bindResizeEvents()
		bindIframeWheelPassthroughEvents()
	},
	getInstance: function () {
		return bodyLenis
	},
	isBodySmoothScrollEnabled: function () {
		return isBodySmoothScrollEnabled()
	},
	/** Stop or start body Lenis (e.g. when horizontal scroll is active on main page so Lenis must not handle wheel) */
	setBodyLenisStopped: function (stopped) {
		if (bodyLenis && typeof bodyLenis.isStopped !== 'undefined') {
			// Prefer Lenis stop/start methods so the stop state is applied reliably
			// across Lenis versions / client-side differences.
			if (stopped) {
				if (typeof bodyLenis.stop === 'function') {
					bodyLenis.stop()
				}
				bodyLenis.isStopped = true
			} else {
				if (typeof bodyLenis.start === 'function') {
					bodyLenis.start()
				}
				bodyLenis.isStopped = false
			}
		}
	},
	stop: function () {
		addBodyLock('manual')
	},
	start: function () {
		removeBodyLock('manual')
	},
	scrollTo: function (target, options) {
		// Only scroll if smooth scroll is enabled and bodyLenis exists
		if (isBodySmoothScrollEnabled() && bodyLenis) {
			bodyLenis.scrollTo(target, options)
		}
	},
	addBodyLock: function (reason) {
		addBodyLock(reason)
	},
	removeBodyLock: function (reason) {
		removeBodyLock(reason)
	},
}

// Make it globally available
if (typeof window !== 'undefined') {
	window.smooth_scroll_controller = smooth_scroll_controller
}

export default smooth_scroll_controller
